import React from 'react'

import { FiltersPanel } from './rooms-pane/FiltersPanel'
import { OfficeSelect } from './rooms-pane/OfficeSelect'
import { RoomGroupSectionItem } from './rooms-pane/RoomGroupSection'
import { AppShell, AttachErrorMessage, EmptyState, RoomsSection, Toolbar } from './rooms-pane/styles'

import type { OfficeOption, RoomAvailability, RoomFilterTag, RoomGroup } from '../models/rooms'

type Props = {
    offices: OfficeOption[]
    selectedOffice: OfficeOption | null
    searchTerm: string
    isOfficeOpen: boolean
    isFilterOpen: boolean
    availableTags: RoomFilterTag[]
    activeTagIds: Set<string>
    minCapacity: number | null
    capacityOptions: number[]
    filteredGroups: RoomGroup[]
    roomAvailabilityById: Record<string, RoomAvailability>
    roomsLoadingMessage: string
    roomsLoaded: boolean
    attachedRoomId: string | null
    attachedRoomLabel?: string
    attachError: string | null
    attachingRoomId: string | null
    startTimeLabel: string
    endTimeLabel: string
    scheduleEditor?: {
        dateValue: string
        startTimeValue: string
        endTimeValue: string
        onDateChange: (value: string) => void
        onStartTimeChange: (value: string) => void
        onEndTimeChange: (value: string) => void
    }
    officeSelectRef: React.RefObject<HTMLDivElement | null>
    filterPanelRef: React.RefObject<HTMLDivElement | null>
    filterTriggerRef: React.RefObject<HTMLButtonElement | null>
    onSearchChange: (value: string) => void
    onMinCapacityChange: (value: number | null) => void
    onToggleOfficeOpen: () => void
    onToggleFilterOpen: () => void
    onSelectOffice: (officeId: string) => void
    onToggleTag: (tagId: string) => void
    onClearTags: () => void
    onToggleGroup: (groupId: string) => void
    onAttachRoom: (roomId: string) => void
}

export function RoomsPane({
    offices,
    selectedOffice,
    searchTerm,
    isOfficeOpen,
    isFilterOpen,
    availableTags,
    activeTagIds,
    minCapacity,
    capacityOptions,
    filteredGroups,
    roomAvailabilityById,
    roomsLoadingMessage,
    roomsLoaded,
    attachedRoomId,
    attachedRoomLabel,
    attachError,
    attachingRoomId,
    startTimeLabel,
    endTimeLabel,
    scheduleEditor,
    officeSelectRef,
    filterPanelRef,
    filterTriggerRef,
    onSearchChange,
    onMinCapacityChange,
    onToggleOfficeOpen,
    onToggleFilterOpen,
    onSelectOffice,
    onToggleTag,
    onClearTags,
    onToggleGroup,
    onAttachRoom,
}: Props) {
    return (
        <AppShell>
            <Toolbar>
                <OfficeSelect
                    offices={offices}
                    selectedOffice={selectedOffice}
                    isOpen={isOfficeOpen}
                    containerRef={officeSelectRef}
                    onToggle={onToggleOfficeOpen}
                    onSelect={onSelectOffice}
                />

                <FiltersPanel
                    searchTerm={searchTerm}
                    isOpen={isFilterOpen}
                    availableTags={availableTags}
                    activeTagIds={activeTagIds}
                    minCapacity={minCapacity}
                    capacityOptions={capacityOptions}
                    startTimeLabel={startTimeLabel}
                    endTimeLabel={endTimeLabel}
                    scheduleEditor={scheduleEditor}
                    filterPanelRef={filterPanelRef}
                    filterTriggerRef={filterTriggerRef}
                    onSearchChange={onSearchChange}
                    onMinCapacityChange={onMinCapacityChange}
                    onToggleOpen={onToggleFilterOpen}
                    onToggleTag={onToggleTag}
                    onClearTags={onClearTags}
                />
            </Toolbar>

            <RoomsSection aria-live="polite">
                {attachError ? <AttachErrorMessage>{attachError}</AttachErrorMessage> : null}
                {filteredGroups.length ? (
                    filteredGroups.map((group) => (
                        <RoomGroupSectionItem
                            key={group.id}
                            group={group}
                            roomAvailabilityById={roomAvailabilityById}
                            attachedRoomId={attachedRoomId}
                            attachedRoomLabel={attachedRoomLabel}
                            attachingRoomId={attachingRoomId}
                            onToggleGroup={onToggleGroup}
                            onAttachRoom={onAttachRoom}
                        />
                    ))
                ) : (
                    <EmptyState>{roomsLoaded ? 'No rooms match your filters' : roomsLoadingMessage}</EmptyState>
                )}
            </RoomsSection>
        </AppShell>
    )
}
